import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { FormElement, FormId } from "../../../../components/enums";
import {
  FormSchema,
  SimpleFormSchemaComponents,
} from "../../../../components/types";
import { Url } from "../../../../router/enums";
import { selectCountry } from "../../../../store/HOC/genericUI.selectors";
import { subtractFieldsFromObject } from "../../../../utilities/functions";
import { ComponentId as PerpetratorComponentId } from "../PerpetratorData/enums";
import {
  selectPerpetratorIsKnown,
  selectPerpetratorGeneralData,
  selectPerpetratorWasDriver,
  selectPerpetratorWasDriving,
  selectPerpetratorWasNotDriver,
  selectPerpetratorWasNotDriving,
  selectUIData,
} from "../perpetrator.selectors";
import {
  setPerpetrator,
  setPerpetratorCarOwner,
  setPerpetratorId,
  setPerpetratorIsKnown,
  setPerpetratorWasDriving,
} from "../perpetrator.slice";

import { ComponentId } from "./enums";

const useSetPerpetratorCarOwner = (): ((arg: string) => void) => {
  const dispatch = useDispatch();
  const { getValues, errors } = useFormContext();
  const countryValidator = useSelector(
    selectCountry(selectUIData)("perpetratorphone")
  );

  return (carOwner: string) => {
    const payload = {
      [ComponentId.PERPETRATOR_GENERAL_CAR_OWNER]: carOwner,
      countryValidator,
      ...(carOwner !== "company" &&
        subtractFieldsFromObject(getValues(), errors, [
          FormId.PERPETRATOR_DATA,
          [
            PerpetratorComponentId.PERPETRATOR_NAME,
            PerpetratorComponentId.PERPETRATOR_SURNAME,
            PerpetratorComponentId.PERPETRATOR_PHONE,
            PerpetratorComponentId.PERPETRATOR_EMAIL,
          ],
        ])),
      ...(carOwner === "perpetrator" &&
        subtractFieldsFromObject(getValues(), errors, [
          FormId.PERPETRATOR_DATA,
          [
            PerpetratorComponentId.PERPETRATOR_STREET,
            PerpetratorComponentId.PERPETRATOR_STREET_2,
            PerpetratorComponentId.PERPETRATOR_ZIP_CODE_GENERIC,
            PerpetratorComponentId.PERPETRATOR_ZIP_CODE_PL,
            PerpetratorComponentId.PERPETRATOR_CITY,
            PerpetratorComponentId.PERPETRATOR_COUNTRY,
            PerpetratorComponentId.PERPETRATOR_PROVINCE,
          ],
        ])),
    };

    dispatch(setPerpetratorCarOwner(payload));
  };
};

export const main: FormSchema<SimpleFormSchemaComponents> = {
  formId: FormId.PERPETRATOR_GENERAL,
  selector: selectPerpetratorGeneralData,
  redirect: Url.AC_SUMMARY,
  content: [
    {
      title: "Sprawca",
      sections: [
        {
          id: "perpetrator-general-data-1",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_PERPETRATOR,
              required: true,
              label: "Czy osoba kierująca autem Ayvens była sprawcą zdarzenia?",
              onChange: setPerpetrator,
              items: [
                {
                  label: "tak",
                  value: "true",
                },
                {
                  label: "nie",
                  value: "false",
                },
              ],
            },
          ],
        },
        {
          id: "perpetrator-general-data-2",
          isVisible: selectPerpetratorWasDriver,
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_WAS_DRIVER_SOBER,
              required: true,
              label:
                "Czy osoba kierująca autem Ayvens podczas zdarzenia była pod wpływem alkoholu, narkotyków, bądź innych używek?",
              items: [
                {
                  label: "tak",
                  value: "false",
                },
                {
                  label: "nie",
                  value: "true",
                },
              ],
            },
          ],
        },
        {
          id: "perpetrator-general-data-3",
          isVisible: selectPerpetratorWasNotDriver,
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_IS_KNOWN,
              required: true,
              label: "Czy są znane dane sprawcy?",
              onChange: setPerpetratorIsKnown,
              items: [
                {
                  label: "tak",
                  value: "true",
                },
                {
                  label: "nie",
                  value: "false",
                },
              ],
            },
          ],
        },
        {
          id: "perpetrator-general-data-4",
          isVisible: selectPerpetratorIsKnown,
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_WAS_DRIVING,
              required: true,
              label: "Czy sprawca poruszał się pojazdem?",
              onChange: setPerpetratorWasDriving,
              items: [
                {
                  label: "tak",
                  value: "true",
                },
                {
                  label: "nie",
                  value: "false",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export const carOwner: FormSchema<SimpleFormSchemaComponents> = {
  formId: FormId.PERPETRATOR_GENERAL,
  id: [FormId.PERPETRATOR_GENERAL, "car-owner"].join("-"),
  selector: selectPerpetratorGeneralData,
  isVisible: selectPerpetratorWasDriving,
  content: [
    {
      sections: [
        {
          id: "perpetrator-general-car-owner-data",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_CAR_OWNER,
              required: true,
              label:
                "Kto jest właścicielem pojazdu sprawcy zgodnie z dowodem rejestracyjnym?",
              onChange: useSetPerpetratorCarOwner,
              items: [
                {
                  label: "sprawca",
                  value: "perpetrator",
                },
                {
                  label: "inna osoba fizyczna",
                  value: "otherPerson",
                },
                {
                  label: "firma",
                  value: "company",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export const perpetratorId: FormSchema<SimpleFormSchemaComponents> = {
  formId: FormId.PERPETRATOR_GENERAL,
  id: [FormId.PERPETRATOR_GENERAL, "id"].join("-"),
  selector: selectPerpetratorGeneralData,
  isVisible: selectPerpetratorWasNotDriving,
  content: [
    {
      title: "Dane sprawcy",
      sections: [
        {
          id: "perpetrator-general-id-data",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.PERPETRATOR_GENERAL_ID,
              required: true,
              label: "Kim był sprawca?",
              onChange: setPerpetratorId,
              items: [
                {
                  label: "osoba fizyczna",
                  value: "person",
                },
                {
                  label: "firma",
                  value: "company",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
